import { DomainStatus } from './Status';
import { Catalog, PartialLock, PartialSmartLock } from './Customer';
import Community from './Community';
import { AgentsProperties, UserType, UserType as User } from './User';
import { CustomerVisit } from './Visit';
import { TimezoneCode } from '../utils/helpers';

export type BaseProperty = {
  property_id: number;
  address_1: string;
  builder: string;
  state?: string;
  builder_id?: number;
  timezone: TimezoneCode | Catalog;
  subdivision: string;
  house_num: string;
  lock_serial_number: string;
  smart_lock_serial_number: string;
  grouping?: string;
  propertyStatusId?: string;
  url_1?: string;
  rental_application_url?: string;
  lock?: PartialLock | Lock;
  smartLock?: PartialSmartLock | SmartLock;
  client?: {
    requires_opt_in?: boolean;
    name?: string;
  };
  timezoneCatalog?: {
    catalog_id?: TimezoneCode;
    value?: string;
  };
  agents?: Array<{
    user: UserType;
    agent_username?: string;
    is_project_manager: boolean;
  }>;
  hub?: {
    serial_number: string;
    hub_type: string;
    hub_account_id: string;
  };
  sunParams?: SunParams;
  adjustableTourHours: number;
  tourHoursStart?: number;
  tourHoursEnd?: number;
  propertyTourHours?: {
    dayOfWeek: number;
    tourHoursStart: string;
    tourHoursEnd: string;
    isToursAvailable: string
    secondWindowStart?: string;
    secondWindowEnd?: string;
    isSecondWindowEnabled?: boolean
  };
  propertyCommunity?: {
    communities?: Community
  }
  alarm?: boolean;
  alarm_company?: string | null;
  armed_hours?: string | null;
  disarm_code?: string | null;
  emergency_notes?: string | null;
  marketing_name?: string | null;
  novihome_integration?: string | null;
  nternow_hub_backup_code?: string | null;
  property_backup_code?: string | null;
  temp_backup_code?: string | null;
  access_notes?: string | null;
  access_settings?: boolean | null,
  relock_interval?: string | null,
  end_of_day_lockup?: string | null,
  emergency_contact?: {
    username: string | null,
    firstname: string | null,
    lastname: string | null,
    phone: string | null
  },
};

export type SunParams = {
  sunrise: string;
  sunset: string;
  untilSunrise: string;
  untilSunset: string;
  isBeforeSunrise: boolean;
  isDuringDaytime: boolean;
  isAfterSunset: boolean;
};
export interface BaseLock {
  serial_number: string;
  status?: DomainStatus;
  lockStatusId?: string;
  battery_change: Date;
  comments: string;
  version: string;
  purchase: Date;
  additional_info_1: string;
  manufacturer: string;
  licensee: string;
  license_start_date: Date;
  created_on: string;
  created_by: string;
  modified_on: string;
  modified_by: string;
}
export interface Lock extends BaseLock {
  property?: Property;
  lockStatus?: string;
  year: number;
  batch: string;
  hardware_version: string;
  software_version: string;
  client?: { builder_id?: number; name?: string };
}
export type SmartLockConnectionMode = 'wifi' | 'ble';

export const isBleLock = (
  smartLock?: { connection_mode?: SmartLockConnectionMode | null } | null,
): boolean => smartLock?.connection_mode === 'ble';

export interface BaseSmartLock {
  serial_number: string;
  device_id: String;
  connection_mode?: SmartLockConnectionMode | null;
  status?: DomainStatus;
  smartLockStatusId?: string;
  comments: string;
  created_on: string;
  created_by: string;
  modified_on: string;
  modified_by: string;
}
export interface SmartLock extends BaseSmartLock {
  property?: Property;
  smartLockStatus?: string;
  client?: { builder_id?: number; name?: string };
}

export interface Shipments {
  shipments: Array<Shipment>;
}

export interface BaseShipment {
  lockSerialNumber?: string[];
  cost: number;
  qty?: number;
  carrier?: string;
  date?: Date;
  tracking_number?: string;
  builder_id?: number;
  username?: string;
  status_id?: string;
  number?: number;
  replacement_lock_number?: string;
  requested_by?: string;
  notes?: string;
}

export interface PaginatedShipment extends BaseShipment {
  id: number;
}

export interface Shipment extends BaseShipment {
  id?: number;
}

export interface Property extends BaseProperty {
  lock_assigned: string;
  lock_terminated: string | null;
  address_2: number;
  client?: {
    name: string;
    requires_opt_in?: boolean;
  };
  propertyCommunity?: { communities: Community };
  disposition?: string;
  propertyStatusId?: string;
  community?: Community;
  lock: Lock;
  smartLock: SmartLock;
  visits: CustomerVisit[];
  availableAgents: User[];
  assignedAgents: AgentsProperties[];
  status?: {
    status_id?: string;
  };
  hub?: {
    serial_number: string;
    hub_type: string;
    hub_account_id: string;
  };
  hs?: string;
  state: string;
  city: string;
  remarks?: string;
  url_1: string;
  rental_application_url: string;
  zipcode: string;
  latitude: number;
  builder_id: number;
  longitude: number;
  is_rental: boolean;
  is_webflow_code_only: boolean;
  house_num: string;
  modified_on: Date;
  modified_by?: string;
  LASSO_project_id: string | null;
  contract_date?: Date;
  price: number;
  bedrooms: number;
  halfBathrooms: number;
  fullBathrooms: number;
  squareFootage: number;
  isModel: boolean;
  community_id: number;
  alarm: boolean;
  alarm_company: string | null;
  armed_hours: string | null;
  disarm_code: string | null;
  emergency_notes: string | null;
  marketing_name?: string | null;
  novihome_integration?: string | null;
  nternow_hub_backup_code?: string | null;
  property_backup_code?: string | null;
  temp_backup_code?: string | null;
  trade_code?: string | null;
  sales_code?: string | null;
  resident_code?: string | null;
  wifiRouter?: {
    serial_number?: string | null;
  }
  hardcoded_code?: string | null;
  mls_number?: string | null;
  external_identifier?: string | null;
  external_identifier_2?: string | null;
}
